import React, { useEffect, useState } from 'react'
import type { LocationDetail } from '../../../../../types/location'
import { useLocationUpdate } from '../../../hooks/useLocationUpdate'
import { getApiErrorMessage } from '../../../utils/getApiErrorMessage'
import {
  toTaxDecimalString,
  toTaxInputValue,
  validateTaxPercent,
} from '../../../utils/shippingTax'

interface Props {
  open: boolean
  location: LocationDetail
  onClose: () => void
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 32px 8px 12px',
  fontSize: 13,
  border: '1px solid #E5E7EB',
  borderRadius: 8,
  outline: 'none',
  color: '#111827',
  background: '#fff',
  boxSizing: 'border-box',
}

const unitStyle: React.CSSProperties = {
  position: 'absolute',
  right: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: 14,
  color: '#33363D66',
  pointerEvents: 'none',
}

const btnBase: React.CSSProperties = {
  padding: '8px 18px',
  fontSize: 13.5,
  borderRadius: 8,
}

export const EditShippingTaxDialog: React.FC<Props> = ({ open, location, onClose }) => {
  const { mutate: updateDetails, isPending } = useLocationUpdate()
  const [value, setValue] = useState('0.00')
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setValue(toTaxInputValue(location.shippingTaxPercentage) || '0.00')
      setSaveError(null)
    }
  }, [open, location])

  if (!open) return null

  const error = validateTaxPercent(value)
  const canSave = !error && !isPending
  const customZones = location.mailOrderZones.filter(
    (z) => z.shipping_tax_percentage != null && z.shipping_tax_percentage !== '',
  ).length
  const inheritingZones = location.mailOrderZones.length - customZones

  const handleSave = () => {
    if (!canSave) return
    setSaveError(null)
    updateDetails(
      {
        warehouseId: location.id,
        data: { shipping_tax_percentage: toTaxDecimalString(value) },
      },
      {
        onSuccess: onClose,
        onError: (err) =>
          setSaveError(getApiErrorMessage(err, "Couldn't save shipping tax. Try again.")),
      },
    )
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-shipping-tax-title"
        style={{
          position: 'relative', background: '#fff', borderRadius: 14,
          width: 440, maxWidth: 'calc(100vw - 32px)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 40px rgba(0,0,0,0.18)', overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '18px 24px', borderBottom: '1px solid #E5E7EB',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h3 id="edit-shipping-tax-title" style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' }}>
            Edit Shipping Tax
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            aria-label="Close"
            style={{
              border: 'none', background: 'none', cursor: 'pointer',
              color: '#9CA3AF', fontSize: 20, lineHeight: 1, padding: '2px 6px',
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 24 }}>
          <label htmlFor="location-shipping-tax" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Location default shipping tax
          </label>
          <div style={{ position: 'relative', maxWidth: 200 }}>
            <input
              id="location-shipping-tax"
              type="number"
              inputMode="decimal"
              min={0}
              max={100}
              step={0.01}
              value={value}
              onChange={(e) => {
                setSaveError(null)
                setValue(e.target.value)
              }}
              aria-invalid={!!error}
              style={{ ...inputStyle, borderColor: error ? '#FCA5A5' : '#E5E7EB' }}
              disabled={isPending}
            />
            <span style={unitStyle}>%</span>
          </div>
          {error ? (
            <p role="alert" style={{ margin: '6px 0 0', fontSize: 12, color: '#DC2626' }}>{error}</p>
          ) : (
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#6B7280', lineHeight: 1.5 }}>
              Applies to delivery and mail order shipping fees (not pickup).{' '}
              {location.mailOrderZones.length > 0 &&
                `${inheritingZones} of ${location.mailOrderZones.length} mail order zone${
                  location.mailOrderZones.length !== 1 ? 's' : ''
                } use this rate${customZones > 0 ? '; zones with a custom rate are unaffected' : ''}.`}
            </p>
          )}
          {saveError && (
            <div role="alert" style={{
              marginTop: 12, padding: '8px 12px', fontSize: 12, color: '#B91C1C',
              background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8,
            }}>
              {saveError}
            </div>
          )}
        </div>

        <div style={{
          padding: '16px 24px', borderTop: '1px solid #E5E7EB',
          display: 'flex', justifyContent: 'flex-end', gap: 10,
        }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            style={{
              ...btnBase, fontWeight: 500, border: '1px solid #E5E7EB',
              background: '#fff', color: '#374151',
              cursor: isPending ? 'not-allowed' : 'pointer', opacity: isPending ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            style={{
              ...btnBase, fontWeight: 600, border: 'none',
              background: canSave ? '#2563EB' : '#93C5FD', color: '#fff',
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
          >
            {isPending ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}
